// src/pages/Projects.tsx

import React, { useEffect, useState } from 'react'
import { Button, Container, Form, Row, Col } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { useFetchUserProfileQuery } from '../services/authApi'
import ProjectCard from '../components/ProjectCard'
import { Project } from '../types/models/project'
import { Model } from '../types/models/model'
import { useFetchProjectsQuery } from '../services/projectsApi'
import CPWizard from '../components/wizards/CPWizard'
import AdminDashboard from '../components/views/AdminDashboard/AdminDashboard'
import { Plus } from 'lucide-react'
import { useAppDispatch } from '../store/hooks'
import { setValidationSource } from '../store/slices/datasetSlice'
import { setTrainingSource } from '../store/slices/datasetSlice'
import { setDatasetSources } from '../store/slices/datasetSlice'
import { clearFilterTags, clearSelectedProjectId } from '../store/slices/projectSlice'
import { setDeployedModels } from '../store/slices/projectSlice'
import { modelsApi } from '../services/modelsApi'

// Teal glassmorphic styling to match app theme
const FiltersBar = styled.div`
  background: linear-gradient(135deg, rgba(34, 211, 238, 0.08) 0%, rgba(20, 184, 166, 0.06) 100%);
  border: 1px solid rgba(34, 211, 238, 0.18);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.04);
`

const SearchInputStyled = styled(Form.Control)`
  background: rgba(26, 26, 26, 0.6);
  border: 1px solid rgba(34, 211, 238, 0.18);
  color: #ffffff;
  border-radius: 10px;
  padding: 0.6rem 0.9rem;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:hover {
    border-color: rgba(34, 211, 238, 0.28);
  }
  &:focus {
    border-color: rgba(34, 211, 238, 0.4);
    box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.18);
    background: rgba(26, 26, 26, 0.7);
    color: #ffffff;
  }
`

// Simplify React-Bootstrap Button typing for styled-components to avoid massive unions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ButtonBase = Button as unknown as React.ComponentType<any>

const PrimaryActionButton = styled(ButtonBase)`
  background: linear-gradient(135deg, #22d3ee 0%, #14b8a6 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 20px rgba(34, 211, 238, 0.35);
  border-radius: 10px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;

  &:hover {
    filter: brightness(1.05);
    box-shadow: 0 10px 24px rgba(34, 211, 238, 0.45);
  }
`

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`

const FilterButton = styled.button<{ $isActive: boolean; $type?: string }>`
  padding: 0.5rem 1.25rem;
  border-radius: 20px;
  border: 1px solid
    ${(props) => (props.$isActive ? 'rgba(34, 211, 238, 0.4)' : 'rgba(255, 255, 255, 0.1)')};
  background: ${(props) =>
    props.$isActive
      ? 'linear-gradient(135deg, rgba(34, 211, 238, 0.2) 0%, rgba(20, 184, 166, 0.15) 100%)'
      : 'rgba(26, 26, 26, 0.4)'};
  color: ${(props) => (props.$isActive ? '#22d3ee' : 'rgba(255, 255, 255, 0.7)')};
  font-size: 0.875rem;
  font-weight: ${(props) => (props.$isActive ? '600' : '500')};
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  backdrop-filter: blur(8px);
  box-shadow: ${(props) =>
    props.$isActive
      ? '0 4px 12px rgba(34, 211, 238, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
      : '0 2px 6px rgba(0, 0, 0, 0.2)'};

  &:hover {
    background: ${(props) =>
      props.$isActive
        ? 'linear-gradient(135deg, rgba(34, 211, 238, 0.25) 0%, rgba(20, 184, 166, 0.2) 100%)'
        : 'rgba(34, 211, 238, 0.1)'};
    border-color: ${(props) =>
      props.$isActive ? 'rgba(34, 211, 238, 0.5)' : 'rgba(34, 211, 238, 0.25)'};
    color: ${(props) => (props.$isActive ? '#22d3ee' : 'rgba(255, 255, 255, 0.9)')};
    transform: translateY(-1px);
    box-shadow: ${(props) =>
      props.$isActive
        ? '0 6px 16px rgba(34, 211, 238, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
        : '0 4px 10px rgba(0, 0, 0, 0.3)'};
  }

  &:active {
    transform: translateY(0);
  }
`

const Projects: React.FC = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { data: user } = useFetchUserProfileQuery()
  const [searchTerm, setSearchTerm] = useState('')
  const [projectTypeFilter, setProjectTypeFilter] = useState('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const { data: projectsData } = useFetchProjectsQuery({})

  // Check if we're viewing the admin dashboard
  const isAdminDashboard = location.pathname.startsWith('/dashboard')

  useEffect(() => {
    const fetchDeployedModels = async () => {
      if (!projectsData?.projects) return

      const allDeployedModels: Model[] = []

      for (const project of projectsData.projects) {
        try {
          const result = await dispatch(
            modelsApi.endpoints.fetchModels.initiate({ project_id: project.id })
          ).unwrap()

          if (result?.models) {
            const deployedModels = result.models.filter(
              (model) => model.deployment?.status.toLowerCase() === 'in_service'
            )
            allDeployedModels.push(...deployedModels)
          }
        } catch (error) {
          console.error(`Failed to fetch models for project ${project.id}:`, error)
        }
      }

      dispatch(setDeployedModels(allDeployedModels))
    }

    fetchDeployedModels()
  }, [projectsData, dispatch])

  useEffect(() => {
    dispatch(clearSelectedProjectId())
    localStorage.removeItem('lastSelectedProjectId')
    dispatch(setTrainingSource([]))
    dispatch(setValidationSource([]))
    dispatch(setDatasetSources([]))
    dispatch(clearFilterTags())
  }, [dispatch])

  const filteredProjects = [...(projectsData?.projects || [])]
    .sort((a, b) => new Date(b.last_accessed).getTime() - new Date(a.last_accessed).getTime())
    .filter((project: Project) => {
      const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesType =
        projectTypeFilter === 'all' || project.annotation_type === projectTypeFilter
      return matchesSearch && matchesType
    })

  const projectTypes = [
    { name: 'All Projects', value: 'all' },
    { name: 'Classification', value: 'classification' },
    { name: 'Bounding Box', value: 'bounding_box' },
    { name: 'Oriented Box', value: 'oriented_bounding_box' },
  ]

  // If viewing admin dashboard, render that instead
  if (isAdminDashboard && user?.username === 'admin') {
    return (
      <Container
        fluid
        className="p-4"
        style={{ background: 'transparent', minHeight: '100vh', paddingBottom: '2rem' }}
      >
        <AdminDashboard />
      </Container>
    )
  }

  return (
    <Container
      fluid
      className="p-4"
      style={{ background: 'transparent', minHeight: '100vh', paddingBottom: '2rem' }}
    >
      <Row className="mb-4">
        <Col>
          <FiltersBar className="d-flex gap-3 align-items-center flex-wrap">
            <SearchInputStyled
              type="text"
              placeholder="Search Projects"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-grow-1"
              style={{ maxWidth: '420px' }}
            />
            <FilterButtonsContainer>
              {projectTypes.map((type) => (
                <FilterButton
                  key={type.value}
                  $isActive={projectTypeFilter === type.value}
                  $type={type.value}
                  onClick={() => setProjectTypeFilter(type.value)}
                >
                  {type.name}
                </FilterButton>
              ))}
            </FilterButtonsContainer>
            <PrimaryActionButton
              variant="primary"
              onClick={() => setShowCreateModal(true)}
              className="d-flex align-items-center gap-2"
              style={{ minWidth: '200px' }}
            >
              <Plus size={20} />
              New Project
            </PrimaryActionButton>
          </FiltersBar>
        </Col>
      </Row>

      <Row>
        <Col>
          {filteredProjects && filteredProjects.length > 0 ? (
            <div className="row g-4">
              {filteredProjects.map((project: Project, index: number) => (
                <div key={index} className="col-xl-3 col-lg-4 col-md-6 col-sm-12">
                  <ProjectCard project={project} />
                </div>
              ))}
            </div>
          ) : (
            <div
              className="text-center py-5"
              style={{
                background:
                  'linear-gradient(135deg, rgba(34, 211, 238, 0.06) 0%, rgba(20, 184, 166, 0.04) 100%)',
                border: '1px solid rgba(34, 211, 238, 0.15)',
                borderRadius: 12,
              }}
            >
              <h4 className="text-white mb-3">No Projects Found</h4>
              <p className="text-muted mb-4">
                {searchTerm
                  ? 'No projects match your search criteria.'
                  : "You don't have any projects yet."}
              </p>
              <PrimaryActionButton variant="primary" onClick={() => setShowCreateModal(true)}>
                {'Create Your First Project'}
              </PrimaryActionButton>
            </div>
          )}
        </Col>
      </Row>

      <CPWizard show={showCreateModal} onHide={() => setShowCreateModal(false)} />
    </Container>
  )
}

export default Projects
